import { TrendingUp } from "lucide-react"
import React, { useMemo, useState } from "react"

import InfoTooltip from "@/components/ui/InfoTooltip"
import { Skeleton } from "@/components/ui/skeleton"
import { cn, formatPercentage } from "@/lib/utils"
import { TablePagingFooter } from "@/components/widgets/common/TablePagingFooter"
import { UserSlaRankingData } from "@/lib/axios/api/widgets.api"
import { WidgetTitle } from "@/components/widgets/WidgetTitle"

const SLA_TARGET = 80
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50]
const SKELETON_ROW_IDS = ["skeleton-1", "skeleton-2", "skeleton-3", "skeleton-4", "skeleton-5"]

function slaColorClass(v: number) {
  if (v >= 80) return "text-success"
  if (v >= 70) return "text-caution"
  return "text-destructive"
}

function slaBarClass(v: number) {
  if (v >= 80) return "bg-success"
  if (v >= 70) return "bg-caution"
  return "bg-destructive"
}

function avgColorClass(avg: number) {
  if (avg >= SLA_TARGET) return "text-success"
  if (avg >= 70) return "text-caution"
  return "text-destructive"
}

function emptyStateMessage(isError: boolean, hasData: boolean, agentCount: number) {
  if (isError) return "No data"
  if (!hasData) return "No call records were found"
  if (agentCount === 0) return "No data available"
  return "Everyone is on target"
}

interface UserSlaRankingWidgetProps {
  data?: UserSlaRankingData
  isLoading?: boolean
  isError?: boolean
}

const UserSlaRankingWidget: React.FC<UserSlaRankingWidgetProps> = ({
  data,
  isLoading = false,
  isError = false,
}) => {
  const [view, setView] = useState<"all" | "attention">("all")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(5)

  const agents = data?.agents ?? []
  const avg = data?.avgSla ?? 0
  const totalAgent = data?.totalAgent ?? 0
  const agentNeedAttention = data?.agentNeedAttention ?? 0
  const unfilteredNumber = data?.unfilteredNumber ?? totalAgent
  // TCX-fix-role: the header pairs the numerator with unfilteredNumber's own pinned, full-team scope —
  // agentNeedAttention itself still narrows with the filter (drives the per-agent list below).
  const unfilteredAgentNeedAttention = data?.unfilteredAgentNeedAttention ?? agentNeedAttention

  const okRows = useMemo(
    () =>
      agents
        .filter((u) => u.sla >= SLA_TARGET)
        .sort((a, b) => b.sla - a.sla || a.agentName.localeCompare(b.agentName)),
    [agents]
  )
  const needRows = useMemo(
    () =>
      agents
        .filter((u) => u.sla < SLA_TARGET)
        .sort((a, b) => a.sla - b.sla || a.agentName.localeCompare(b.agentName)),
    [agents]
  )

  const belowCount = needRows.length
  const ordered = view === "attention" ? needRows : [...okRows, ...needRows]
  const totalPages = Math.max(1, Math.ceil(ordered.length / pageSize))
  const pg = Math.min(Math.max(1, page), totalPages)
  const start = (pg - 1) * pageSize
  const slice = ordered.slice(start, start + pageSize)

  const setViewReset = (v: "all" | "attention") => {
    setView(v)
    setPage(1)
  }
  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setPage(1)
  }

  let prevBand: "ok" | "need" | null = null

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border border-border bg-card shadow-sm dark:border-primary-fixed dark:bg-card overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border border-b-0 bg-white px-2 py-2 shrink-0 dark:bg-card">
        <TrendingUp className="w-4 h-4 text-foreground shrink-0" />
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <WidgetTitle className="truncate">SLA Ranking</WidgetTitle>
          <InfoTooltip content="Users ranked by SLA attainment. Anyone below the 80% target is flagged for attention" />
        </div>
        <div className="inline-flex items-center gap-0.5 rounded-[9px] border border-border dark:border-0 bg-[#F5F7FA] dark:bg-[#3C5C73] p-[3px]">
          <button
            type="button"
            onClick={() => setViewReset("all")}
            className={cn(
              "rounded-[7px] px-2.5 py-1 text-xs font-semibold transition-colors",
              view === "all"
                ? "bg-brand-accent text-primary-fixed shadow-sm"
                : "text-muted-foreground hover:text-foreground dark:text-white/70 dark:hover:text-white"
            )}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setViewReset("attention")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-[7px] px-2.5 py-1 text-xs font-semibold transition-colors",
              view === "attention"
                ? "bg-brand-accent text-primary-fixed shadow-sm"
                : "text-muted-foreground hover:text-foreground dark:text-white/70 dark:hover:text-white"
            )}
          >
            Needs attention{" "}
            <span
              className={cn(
                "min-w-[18px] rounded-full px-1 text-center text-[10px] font-bold",
                view === "attention"
                  ? "bg-[hsl(207,81%,18%)]/20 text-primary-fixed"
                  : "bg-destructive text-destructive-foreground"
              )}
            >
              {belowCount}
            </span>
          </button>
        </div>
      </div>

      {/* Summary strip — hidden during loading / error */}
      {!isLoading && !isError && data !== undefined && (
        <div className="flex items-center gap-3 border-y border-border bg-muted/30 h-[43px] px-4 text-xs text-muted-foreground shrink-0 dark:text-white dark:border-white/35">
          <span>
            Target{" "}
            <strong className="text-success">{SLA_TARGET}%</strong>
          </span>
          <span>
            Avg{" "}
            <strong className={cn(avgColorClass(avg))}>
              {formatPercentage(avg)}
            </strong>
          </span>
          <span className={unfilteredAgentNeedAttention > 0 ? "text-destructive font-medium" : ""}>
            <strong>{unfilteredAgentNeedAttention}</strong> of {unfilteredNumber} need attention
          </span>
        </div>
      )}

      {/* Rows */}
      <div className="flex-1 min-h-0 overflow-auto px-3 py-2 flex flex-col gap-0.5">
        {isLoading && SKELETON_ROW_IDS.map((id) => (
          <div
            key={id}
            className="grid items-center gap-3 py-[7px] px-1"
            style={{ gridTemplateColumns: "160px 1fr 48px" }}
          >
            <Skeleton className="h-3 w-[120px]" />
            <Skeleton className="h-2.5 rounded-full" />
            <Skeleton className="h-3 w-[36px] ml-auto" />
          </div>
        ))}
        {!isLoading && (isError || slice.length === 0) && (
          <div className="flex-1 flex items-center justify-center text-sm text-foreground py-6">
            {emptyStateMessage(isError, data !== undefined, agents.length)}
          </div>
        )}
        {!isLoading && slice.map((u) => {
          const ok = u.sla >= SLA_TARGET
          const band: "ok" | "need" = ok ? "ok" : "need"
          const showHd = band !== prevBand
          prevBand = band
          return (
            <div key={u.agentName}>
              {showHd && (
                <div className="flex items-center gap-2 mt-2 mb-1 px-1">
                  <span
                    className={cn(
                      "w-2 h-2 rounded-full shrink-0",
                      ok ? "bg-success" : "bg-destructive"
                    )}
                  />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {ok ? "On target" : "Needs attention"}
                  </span>
                  <span
                    className={cn(
                      "rounded-full border border-border bg-muted px-1.5 text-[10px] dark:border-white/20 dark:bg-white/10 dark:text-white",
                      !ok && belowCount > 0
                        ? "border-destructive/30 bg-destructive/10 text-destructive dark:border-destructive/40 dark:bg-destructive/20 dark:text-destructive"
                        : ""
                    )}
                  >
                    {ok ? okRows.length : needRows.length}
                  </span>
                </div>
              )}
              <div
                className="grid items-center gap-3 py-[7px] px-1"
                style={{ gridTemplateColumns: "160px 1fr 48px" }}
              >
                <span
                  className="text-xs text-foreground leading-tight truncate dark:text-white cursor-default"
                  title={u.agentName}
                >
                  {u.agentName}
                </span>
                <div className="relative h-2.5 rounded-full bg-border overflow-hidden">
                  <div
                    className={cn("absolute inset-y-0 left-0 rounded-full", slaBarClass(u.sla))}
                    style={{ width: `${u.sla}%` }}
                  />
                </div>
                <span className={cn("text-xs text-right", slaColorClass(u.sla))}>
                  {formatPercentage(u.sla)}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      <TablePagingFooter
        currentPage={pg}
        totalPages={totalPages}
        total={ordered.length}
        pageSize={pageSize}
        onPageChange={setPage}
        isLoading={isLoading}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}

export default UserSlaRankingWidget
